'use client';

import Link from 'next/link';
import { redirect } from 'next/navigation';
import { type FC, type PropsWithChildren, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import GeneralSkeleton from '@/components/skeletons/GeneralSkeleton';
import { useNavigation } from '@/hooks/navigation';
import { useCMSConfig } from '@/modules/common';
import { checkIsCmsPromoAvailable } from '@/modules/promotions';
import { useGetCommonStore } from '@/store';
import { someGroupMember, type USER_GROUP } from './user';

export function withAuth<T>(Component: FC<T>) {
  return function WithAuth(props: T) {
    const useCommonStore = useGetCommonStore();
    const user = useCommonStore.use.user();
    const pendingUser = useCommonStore.use.pendingUser();

    useEffect(() => {
      if (user || pendingUser) return;
      redirect('/');
    }, [user, pendingUser]);

    if (!user) return <GeneralSkeleton />;

    // @ts-ignore
    return <Component {...props} />;
  };
}

interface WithGuardProps {
  userGroups: USER_GROUP[];
  redirectUrl: string;
  checkIsHiddenPage?: () => boolean;
  showIfNotAuthenticated?: boolean;
}

export function withGuard<T extends object>(
  Component: FC<T>,
  options: WithGuardProps
) {
  return function WithGuard(props: T) {
    const useCommonStore = useGetCommonStore();

    const { isAuthenticated, pendingUser, user, pendingStore } = useCommonStore(
      useShallow((state) => ({
        isAuthenticated: state.isAuthenticated,
        pendingUser: state.pendingUser,
        user: state.user,
        pendingStore: state.pendingStore,
      }))
    );

    const {
      showIfNotAuthenticated,
      redirectUrl,
      checkIsHiddenPage,
      userGroups,
    } = options;

    const hiddenPage = checkIsHiddenPage && checkIsHiddenPage();

    useEffect(() => {
      if (
        (hiddenPage ||
          !(isAuthenticated || showIfNotAuthenticated) ||
          (user && someGroupMember(userGroups)(user))) &&
        !pendingStore
      )
        redirect(redirectUrl);
    }, [
      isAuthenticated,
      user,
      hiddenPage,
      redirectUrl,
      showIfNotAuthenticated,
      userGroups,
      pendingStore,
    ]);

    if (pendingUser && !isAuthenticated) return <GeneralSkeleton />;

    return <Component {...props} />;
  };
}

interface LinkWrapperProps extends PropsWithChildren {
  withLink: boolean;
  href: string;
  rel?: string;
  onClick?: () => void;
}

export const LinkWrapper: FC<LinkWrapperProps> = ({
  withLink,
  href,
  children,
  ...props
}) =>
  withLink ? (
    <Link href={href} prefetch={false} {...props}>
      {children}
    </Link>
  ) : (
    children
  );

interface WithRestrictionsProps {
  cb: () => boolean;
}

export function withRestrictions<T extends object>(
  Component: FC<T>,
  options: WithRestrictionsProps
) {
  return function WithR(props: T) {
    if (options.cb()) return <Component {...props} />;
    return null;
  };
}

interface WithPromoProps {
  redirectUrl: string;
  promoName:
    | 'bonanzaPromo'
    | 'scratchGame'
    | 'fortuneWheel'
    | 'diceRoll'
    | 'quest'
    | 'mission';
  restrictedGroups?: USER_GROUP[];
}

export function withPromo<T extends object>(
  Component: FC<T>,
  options: WithPromoProps
) {
  return function WithGuard(props: T) {
    const { cmsConfig } = useCMSConfig();
    const useCommonStore = useGetCommonStore();

    const { user } = useCommonStore(
      useShallow((state) => ({
        user: state.user,
      }))
    );

    const { navigate } = useNavigation();

    useEffect(() => {
      if (
        checkIsCmsPromoAvailable(
          cmsConfig?.[options.promoName]?.availableByDate,
          user,
          options.restrictedGroups
        )
      )
        return;

      navigate(options.redirectUrl);
    }, [cmsConfig, user, navigate]);

    return <Component {...props} />;
  };
}
